/**
 * Projections a printed sheet can be gridded and aligned to.
 *
 * Mirrors `PrintMaps::Crs` on the server. The raster underneath is always Web
 * Mercator, but for a projected CRS the sheet is rotated to *grid* north so
 * the kilometre lattice sits square to the paper and the eastings/northings
 * read straight off the edge — the same thing an OS sheet does.
 *
 * Grid convergence is measured from the projection itself (step a kilometre
 * up the grid, convert back, take the geodetic azimuth) rather than from a
 * per-projection formula, so it stays right anywhere in the zone.
 */
import proj4 from 'proj4';

export type ProjectionKey = 'web-mercator' | 'latlon' | 'latlon_dms' | 'gb' | 'ie' | 'nz' | 'utm';

export interface ProjectionDefinition {
  key: ProjectionKey;
  label: string;
  shortLabel: string;
  /** proj4 string, or null for UTM where the zone comes from the location. */
  proj4: string | null;
  metric: boolean;
  /** [west, south, east, north] the projection is usable within. */
  validity: [number, number, number, number] | null;
  /** Matching value for the user's grid-reference preference. */
  gridCrs: string;
}

export const WGS84 = '+proj=longlat +datum=WGS84 +no_defs';

export const PROJECTIONS: ProjectionDefinition[] = [
  {
    key: 'web-mercator',
    label: 'Web Mercator (north up)',
    shortLabel: 'Mercator',
    proj4: null,
    metric: false,
    validity: null,
    gridCrs: 'LatLon',
  },
  {
    key: 'gb',
    label: 'British National Grid (OS)',
    shortLabel: 'OSGB',
    proj4:
      '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy ' +
      '+towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs',
    metric: true,
    validity: [-9.0, 49.0, 3.0, 61.5],
    gridCrs: 'GbGrid',
  },
  {
    key: 'ie',
    label: 'Irish Grid',
    shortLabel: 'Irish Grid',
    proj4:
      '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=1.000035 +x_0=200000 +y_0=250000 +ellps=mod_airy ' +
      '+towgs84=482.5,-130.6,564.6,-1.042,-0.214,-0.631,8.15 +units=m +no_defs',
    metric: true,
    validity: [-11.5, 51.0, -5.0, 56.0],
    gridCrs: 'IeGrid',
  },
  {
    key: 'nz',
    label: 'New Zealand Map Grid',
    shortLabel: 'NZMG',
    proj4:
      '+proj=nzmg +lat_0=-41 +lon_0=173 +x_0=2510000 +y_0=6023150 +ellps=intl ' +
      '+towgs84=59.47,-5.04,187.44,0.47,-0.1,1.024,-4.5993 +units=m +no_defs',
    metric: true,
    validity: [165.0, -48.5, 180.0, -33.0],
    gridCrs: 'NzGrid',
  },
  {
    key: 'utm',
    label: 'UTM (worldwide)',
    shortLabel: 'UTM',
    proj4: null,
    metric: true,
    validity: [-180.0, -80.0, 180.0, 84.0],
    gridCrs: 'LatLon',
  },
];

export function findProjection(key: string | null | undefined): ProjectionDefinition {
  return PROJECTIONS.find((projection) => projection.key === key) ?? PROJECTIONS[0];
}

export function utmZone(lng: number): number {
  return (Math.floor((lng + 180) / 6) % 60) + 1;
}

export function utmProj4(lng: number, lat: number): string {
  return `+proj=utm +zone=${utmZone(lng)}${lat < 0 ? ' +south' : ''} +datum=WGS84 +units=m +no_defs`;
}

export function projectionString(definition: ProjectionDefinition, lng: number, lat: number): string | null {
  if (definition.key === 'utm') return utmProj4(lng, lat);
  return definition.proj4;
}

export function isUsableAt(definition: ProjectionDefinition, lng: number, lat: number): boolean {
  if (!definition.validity) return true;
  const [west, south, east, north] = definition.validity;
  return lng >= west && lng <= east && lat >= south && lat <= north;
}

const toDegrees = (radians: number): number => (radians * 180) / Math.PI;
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** Initial geodetic bearing from one point to another, in degrees. */
export function azimuth(fromLng: number, fromLat: number, toLng: number, toLat: number): number {
  const phi1 = toRadians(fromLat);
  const phi2 = toRadians(toLat);
  const delta = toRadians(toLng - fromLng);
  const y = Math.sin(delta) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(delta);
  return ((toDegrees(Math.atan2(y, x)) % 360) + 360) % 360;
}

/**
 * True bearing of grid north at a point, i.e. the map bearing that puts grid
 * north at the top of the page. Zero for Web Mercator or outside the
 * projection's usable area.
 */
export function gridNorthBearing(key: string | null | undefined, lng: number, lat: number): number {
  const definition = findProjection(key);
  if (!definition.metric) return 0;
  if (!isUsableAt(definition, lng, lat)) return 0;

  const projection = projectionString(definition, lng, lat);
  if (!projection) return 0;

  try {
    const [easting, northing] = proj4(WGS84, projection, [lng, lat]);
    const [upLng, upLat] = proj4(projection, WGS84, [easting, northing + 1000]);
    return azimuth(lng, lat, upLng, upLat);
  } catch {
    return 0;
  }
}

/**
 * Signed rotation, -180..180, for display ("grid north 1.4° E of true").
 *
 * Double-modulo because JavaScript's `%` keeps the sign of the dividend, so a
 * single `(x + 540) % 360` returns a negative remainder — and therefore a
 * result outside -180..180 — once the input drops below -540. That never
 * happens for a compass bearing, but `bearingDelta` feeds this a difference
 * of two bearings, which can.
 */
export function signedBearing(bearing: number): number {
  return ((((bearing + 180) % 360) + 360) % 360) - 180;
}

/**
 * Shortest turn from `from` to `to`, in -180..180.
 *
 * Compass bearings run 0..360 but Mapbox reports its own in -180..180, so
 * subtracting them directly makes 357 and -3 look 360 degrees apart rather
 * than 6. Comparing raw values that way made the page believe the map still
 * needed rotating no matter how many times it rotated it.
 */
export function bearingDelta(from: number, to: number): number {
  return signedBearing(to - from);
}
